import { useState } from 'react';
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.vennio.app';

const QUESTIONS = [
  {
    id: 'q1',
    text: 'What are you building?',
    options: [
      { label: 'Marketplace', value: 'marketplace' },
      { label: 'SaaS', value: 'saas' },
      { label: 'Internal tool', value: 'internal' },
      { label: 'AI agent', value: 'ai_agent' },
    ],
  },
  {
    id: 'q2',
    text: 'How do you handle double-bookings?',
    options: [
      { label: 'Prevented at DB level', value: 'db' },
      { label: 'App-level check', value: 'app' },
      { label: 'Manual review', value: 'manual' },
      { label: 'No protection', value: 'none' },
    ],
  },
  {
    id: 'q3',
    text: 'Do you serve multiple timezones?',
    options: [
      { label: 'Yes, with explicit timezone handling', value: 'explicit' },
      { label: 'Yes, but using server time', value: 'server' },
      { label: 'No', value: 'no' },
    ],
  },
  {
    id: 'q4',
    text: 'What happens when a user cancels?',
    options: [
      { label: 'Auto-filled from waitlist', value: 'auto' },
      { label: 'Manual rescheduling', value: 'manual' },
      { label: 'Nothing', value: 'nothing' },
    ],
  },
  {
    id: 'q5',
    text: 'Are cron/scheduled jobs monitored and retried on failure?',
    options: [
      { label: 'Yes — alerts + retry logic', value: 'alerts_retry' },
      { label: 'Logs only', value: 'logs' },
      { label: 'Fire-and-forget', value: 'fire_forget' },
      { label: 'N/A', value: 'na' },
    ],
  },
];

const DIMENSION_LABELS = {
  multiparty: 'Multi-party coordination',
  conflict: 'Conflict detection',
  timezone: 'Timezone handling',
  cancellation: 'Cancellation recovery',
  observability: 'Observability',
};

const DIMENSION_RISKS = {
  multiparty: 'Multi-party scheduling complexity will cause friction at scale',
  conflict: 'Double-bookings at scale will cause customer churn',
  timezone: 'Timezone mismatches will create ghost bookings across regions',
  cancellation: 'Cancellations are leaking revenue — no auto-fill in place',
  observability: 'Cron failures are silent — production bugs go undetected',
};

const GRADE_SUFFIXES = {
  conflict: { none: ' — No double-booking guard detected', manual: ' — Manual process is error-prone' },
  timezone: { server: ' — Server-time TZ causes scheduling drift', no: ' — No TZ support' },
  cancellation: { nothing: ' — No recovery process', manual: ' — Manual process only' },
  observability: { fire_forget: ' — Silent failures undetected', logs: ' — No alerting or retry' },
};

function letterForScore(pts) {
  if (pts >= 18) return 'A';
  if (pts >= 15) return 'B';
  if (pts >= 11) return 'C';
  if (pts >= 7) return 'D';
  return 'F';
}

function scoreAnswers(answers) {
  const dims = {
    multiparty: { q: 'q1', pts: 0 },
    conflict:   { q: 'q2', pts: 0 },
    timezone:   { q: 'q3', pts: 0 },
    cancellation: { q: 'q4', pts: 0 },
    observability: { q: 'q5', pts: 0 },
  };

  dims.multiparty.pts  = { marketplace: 15, saas: 12, internal: 8, ai_agent: 18 }[answers.q1] ?? 0;
  dims.conflict.pts    = { db: 20, app: 12, manual: 4, none: 0 }[answers.q2] ?? 0;
  dims.timezone.pts    = { explicit: 20, server: 5, no: 10 }[answers.q3] ?? 0;
  dims.cancellation.pts = { auto: 20, manual: 8, nothing: 0 }[answers.q4] ?? 0;
  dims.observability.pts = { alerts_retry: 20, logs: 10, fire_forget: 0, na: 5 }[answers.q5] ?? 0;

  const total = Object.values(dims).reduce((s, d) => s + d.pts, 0);
  const percent = Math.round((total / 100) * 100);
  const letter = percent >= 90 ? 'A' : percent >= 75 ? 'B' : percent >= 55 ? 'C' : percent >= 35 ? 'D' : 'F';

  const sorted = Object.entries(dims).sort((a, b) => a[1].pts - b[1].pts);
  const topRisks = sorted.slice(0, 3).map(([key]) => DIMENSION_RISKS[key]);

  const breakdown = Object.entries(dims).map(([key, { pts, q }]) => {
    const dimLetter = letterForScore(pts);
    const answerVal = answers[q];
    const suffix = GRADE_SUFFIXES[key]?.[answerVal] ?? '';
    return `- ${DIMENSION_LABELS[key]}: ${dimLetter}${suffix}`;
  });

  return { percent, letter, topRisks, breakdown };
}

function buildReport(result) {
  const { percent, letter, topRisks, breakdown } = result;
  return [
    `## Scheduling Health Check — Your Score: ${letter} (${percent}/100)`,
    '',
    '### Breakdown',
    ...breakdown,
    '',
    '### Top 3 Risks',
    ...topRisks.map((r, i) => `${i + 1}. ${r}`),
    '',
    '### Fix this with Vennio',
    'Vennio adds conflict detection, cancellation fill, and timezone safety.',
    '→ Start free: vennio.app/auth',
    '→ Docs: docs.vennio.app',
  ].join('\n');
}

const css = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-secondary)',
    padding: '3rem 1rem',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
  },
  inner: {
    maxWidth: '36rem',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  heading: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-3xl)',
    fontWeight: 'var(--fw-bold)',
    color: 'var(--text-primary)',
    margin: 0,
    lineHeight: 1.2,
  },
  sub: {
    fontSize: 'var(--text-lg)',
    color: 'var(--text-secondary)',
    margin: 0,
  },
  fieldset: {
    border: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  legend: {
    fontWeight: 'var(--fw-semibold)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-md)',
    marginBottom: '0.5rem',
    padding: 0,
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: 'var(--text-md)',
    color: 'var(--text-primary)',
  },
  submitBtn: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    background: 'var(--color-accent)',
    color: 'var(--color-paper)',
    border: 'none',
    borderRadius: 0,
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-md)',
    fontWeight: 'var(--fw-semibold)',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  pre: {
    background: 'var(--bg-inverse)',
    color: 'var(--text-inverse)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-sm)',
    padding: '1.5rem',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  emailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  emailLabel: {
    fontSize: 'var(--text-md)',
    color: 'var(--text-secondary)',
    margin: 0,
  },
  emailRow: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  emailInput: {
    flex: '1 1 12rem',
    padding: '0.625rem 0.75rem',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-md)',
    color: 'var(--text-primary)',
    outline: 'none',
    minWidth: 0,
  },
  emailBtn: {
    padding: '0.625rem 1.25rem',
    background: 'var(--color-accent)',
    color: 'var(--color-paper)',
    border: 'none',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--fw-semibold)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  successMsg: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-success)',
  },
  errorMsg: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-error)',
  },
  note: {
    fontSize: 'var(--text-xs)',
    color: 'var(--text-secondary)',
    margin: 0,
  },
};

export default function SchedulingAudit() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const allAnswered = QUESTIONS.every(q => answers[q.id]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!allAnswered) return;
    setResult(scoreAnswers(answers));
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    if (!email || !result) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/v1/audit-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          score_letter: result.letter,
          score_percent: result.percent,
          top_risks: result.topRisks,
          answers,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Something went wrong');
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={css.page}>
      <div style={css.inner}>
        <div>
          <h1 style={css.heading}>Is your scheduling infrastructure production-ready?</h1>
          <p style={{ ...css.sub, marginTop: '0.75rem' }}>
            Answer 5 questions. Get an instant A–F score and plain-text report.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {QUESTIONS.map((q, i) => (
            <fieldset key={q.id} style={css.fieldset}>
              <legend style={css.legend}>{i + 1}. {q.text}</legend>
              {q.options.map(opt => (
                <label key={opt.value} style={css.radioLabel}>
                  <input
                    type="radio"
                    name={q.id}
                    value={opt.value}
                    checked={answers[q.id] === opt.value}
                    onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt.value }))}
                  />
                  {opt.label}
                </label>
              ))}
            </fieldset>
          ))}

          <button
            type="submit"
            style={{ ...css.submitBtn, opacity: allAnswered ? 1 : 0.45, cursor: allAnswered ? 'pointer' : 'not-allowed' }}
            disabled={!allAnswered}
          >
            Run audit
          </button>
        </form>

        {result && (
          <>
            <pre style={css.pre}>{buildReport(result)}</pre>

            <div style={css.emailSection}>
              <p style={css.emailLabel}>Get the full fix guide for your score emailed to you</p>
              {submitted ? (
                <p style={css.successMsg}>Check your inbox — your fix guide is on its way.</p>
              ) : (
                <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={css.emailRow}>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      style={css.emailInput}
                    />
                    <button
                      type="submit"
                      style={{ ...css.emailBtn, opacity: submitting ? 0.6 : 1 }}
                      disabled={!email || submitting}
                    >
                      {submitting ? 'Sending…' : 'Send guide →'}
                    </button>
                  </div>
                  {submitError && <p style={css.errorMsg}>{submitError}</p>}
                  <p style={css.note}>No spam. One email with your fix guide.</p>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
